import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceAPI } from '../../services/api';
import { generateInvoicePDF, generateReportPDF, generateInvoiceListPDF, generateInvoiceListExcel } from '../../utils/invoicePDF';
import './Invoices.css';

const EMPTY_FILTERS = {
  invoiceNumber: '',
  customer: '',
  dateFrom: '',
  dateTo: '',
  status: 'all',
  amountMin: '',
  amountMax: '',
  taxAmountMin: '',
  taxAmountMax: '',
  taxPctMin: '',
  taxPctMax: '',
};

const getTaxTotal = (inv) =>
  parseFloat(inv.cgst_total || 0) +
  parseFloat(inv.sgst_total || 0) +
  parseFloat(inv.igst_total || 0);

const getTaxPct = (inv) => {
  const taxable = parseFloat(inv.total_taxable_value || 0);
  if (taxable === 0) return 0;
  return (getTaxTotal(inv) / taxable) * 100;
};

const fmt = (n) => '₹' + parseFloat(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Invoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await invoiceAPI.getAll();
      if (response.success) setInvoices(response.data);
    } catch (err) {
      setError('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setShowReport(false);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setShowReport(false);
  };

  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).filter(([k, v]) => {
      if (k === 'status') return v !== 'all';
      return v !== '';
    }).length;
  }, [filters]);

  const canGenerateReport = filters.dateFrom !== '' && filters.dateTo !== '';

  // Report: computed from date-range only (ignores other filters)
  const reportData = useMemo(() => {
    if (!canGenerateReport) return null;

    const inRange = invoices.filter((inv) => {
      const d = inv.invoice_date?.split('T')[0] ?? inv.invoice_date;
      return d >= filters.dateFrom && d <= filters.dateTo;
    });

    const cancelled = inRange.filter((inv) => inv.is_cancelled);
    const active    = inRange.filter((inv) => !inv.is_cancelled);

    const sum      = (list, fn) => list.reduce((acc, inv) => acc + fn(inv), 0);
    const sumAmt   = (list) => sum(list, (inv) => parseFloat(inv.grand_total || 0));
    const sumTax   = (list) => sum(list, getTaxTotal);
    const sumTaxable = (list) => sum(list, (inv) => parseFloat(inv.total_taxable_value || 0));
    const sumCgst  = (list) => sum(list, (inv) => parseFloat(inv.cgst_total || 0));
    const sumSgst  = (list) => sum(list, (inv) => parseFloat(inv.sgst_total || 0));
    const sumIgst  = (list) => sum(list, (inv) => parseFloat(inv.igst_total || 0));

    const totalTaxable   = sumTaxable(inRange);
    const totalTaxAmt    = sumTax(inRange);
    const effectivePct   = totalTaxable > 0 ? (totalTaxAmt / totalTaxable) * 100 : 0;

    return {
      dateFrom: filters.dateFrom,
      dateTo:   filters.dateTo,
      total:     { count: inRange.length,    amount: sumAmt(inRange),    tax: sumTax(inRange),    cgst: sumCgst(inRange),    sgst: sumSgst(inRange),    igst: sumIgst(inRange),    taxable: sumTaxable(inRange) },
      cancelled: { count: cancelled.length,  amount: sumAmt(cancelled),  tax: sumTax(cancelled),  cgst: sumCgst(cancelled),  sgst: sumSgst(cancelled),  igst: sumIgst(cancelled),  taxable: sumTaxable(cancelled) },
      active:    { count: active.length,     amount: sumAmt(active),     tax: sumTax(active),     cgst: sumCgst(active),     sgst: sumSgst(active),     igst: sumIgst(active),     taxable: sumTaxable(active) },
      effectivePct,
    };
  }, [invoices, filters.dateFrom, filters.dateTo, canGenerateReport]);

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      if (filters.invoiceNumber && !inv.invoice_number.toLowerCase().includes(filters.invoiceNumber.toLowerCase())) return false;
      if (filters.customer && !inv.customer_name.toLowerCase().includes(filters.customer.toLowerCase())) return false;
      const invDate = inv.invoice_date?.split('T')[0] ?? inv.invoice_date;
      if (filters.dateFrom && invDate < filters.dateFrom) return false;
      if (filters.dateTo && invDate > filters.dateTo) return false;
      if (filters.status === 'active' && inv.is_cancelled) return false;
      if (filters.status === 'cancelled' && !inv.is_cancelled) return false;
      const amount = parseFloat(inv.grand_total);
      if (filters.amountMin !== '' && amount < parseFloat(filters.amountMin)) return false;
      if (filters.amountMax !== '' && amount > parseFloat(filters.amountMax)) return false;
      const tax = getTaxTotal(inv);
      if (filters.taxAmountMin !== '' && tax < parseFloat(filters.taxAmountMin)) return false;
      if (filters.taxAmountMax !== '' && tax > parseFloat(filters.taxAmountMax)) return false;
      const pct = getTaxPct(inv);
      if (filters.taxPctMin !== '' && pct < parseFloat(filters.taxPctMin)) return false;
      if (filters.taxPctMax !== '' && pct > parseFloat(filters.taxPctMax)) return false;
      return true;
    }).sort((a, b) => b.id - a.id);
  }, [invoices, filters]);

  const handleDownload = async (invoiceId) => {
    try {
      const response = await invoiceAPI.getById(invoiceId);
      if (response.success && response.data) {
        generateInvoicePDF(response.data);
      } else {
        alert('Failed to download invoice: No data received');
      }
    } catch (err) {
      alert('Failed to download invoice: ' + (err.message || 'Unknown error'));
    }
  };

  const handleCancel = async (invoice) => {
    const action = invoice.is_cancelled ? 'reinstate' : 'cancel';
    if (window.confirm(`Are you sure you want to ${action} this invoice?`)) {
      try {
        const response = await invoiceAPI.cancel(invoice.id);
        if (response.success) {
          setInvoices(invoices.map((inv) =>
            inv.id === invoice.id ? { ...inv, is_cancelled: response.data.is_cancelled } : inv
          ));
        }
      } catch (err) {
        alert(`Failed to ${action} invoice`);
      }
    }
  };

  const handleDelete = async (invoiceId) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        const response = await invoiceAPI.delete(invoiceId);
        if (response.success) {
          setInvoices(invoices.filter((inv) => inv.id !== invoiceId));
        }
      } catch (err) {
        alert('Failed to delete invoice');
      }
    }
  };

  const handleDownloadReport = () => {
    generateReportPDF(reportData);
  };

  const exportLabel = () => {
    if (filters.dateFrom && filters.dateTo) return `${filters.dateFrom} to ${filters.dateTo}`;
    if (activeFilterCount > 0) return 'Filtered';
    return 'All Invoices';
  };

  const handleExportPDF = () => generateInvoiceListPDF(filtered, exportLabel());
  const handleExportExcel = () => generateInvoiceListExcel(filtered, exportLabel());

  if (loading) return <div className="loading">Loading invoices...</div>;

  const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  return (
    <div className="invoices-container">
      <div className="invoices-header">
        <h2>Invoices</h2>
        <div className="header-actions">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`filter-toggle-btn ${activeFilterCount > 0 ? 'filter-active' : ''}`}
          >
            Filter {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
          </button>
          {filtered.length > 0 && (
            <>
              <button onClick={handleExportExcel} className="export-excel-btn" title="Download Excel">
                Excel
              </button>
              <button onClick={handleExportPDF} className="export-pdf-btn" title="Download PDF">
                PDF
              </button>
            </>
          )}
          <button onClick={() => navigate('/dashboard/invoices/create')} className="create-btn">
            + Create Invoice
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showFilters && (
        <div className="filter-panel">
          <div className="filter-grid">
            <div className="filter-group">
              <label>Invoice No.</label>
              <input type="text" name="invoiceNumber" value={filters.invoiceNumber} onChange={handleFilterChange} placeholder="Search invoice no." />
            </div>
            <div className="filter-group">
              <label>Customer</label>
              <input type="text" name="customer" value={filters.customer} onChange={handleFilterChange} placeholder="Search customer" />
            </div>
            <div className="filter-group">
              <label>Date From</label>
              <input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} />
            </div>
            <div className="filter-group">
              <label>Date To</label>
              <input type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} />
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="filter-group filter-range">
              <label>Amount (₹)</label>
              <div className="range-inputs">
                <input type="number" name="amountMin" value={filters.amountMin} onChange={handleFilterChange} placeholder="Min" min="0" />
                <span>–</span>
                <input type="number" name="amountMax" value={filters.amountMax} onChange={handleFilterChange} placeholder="Max" min="0" />
              </div>
            </div>
            <div className="filter-group filter-range">
              <label>Tax Amount (₹)</label>
              <div className="range-inputs">
                <input type="number" name="taxAmountMin" value={filters.taxAmountMin} onChange={handleFilterChange} placeholder="Min" min="0" />
                <span>–</span>
                <input type="number" name="taxAmountMax" value={filters.taxAmountMax} onChange={handleFilterChange} placeholder="Max" min="0" />
              </div>
            </div>
            <div className="filter-group filter-range">
              <label>Tax % (effective)</label>
              <div className="range-inputs">
                <input type="number" name="taxPctMin" value={filters.taxPctMin} onChange={handleFilterChange} placeholder="Min" min="0" max="100" />
                <span>–</span>
                <input type="number" name="taxPctMax" value={filters.taxPctMax} onChange={handleFilterChange} placeholder="Max" min="0" max="100" />
              </div>
            </div>
          </div>
          <div className="filter-footer">
            <span className="filter-result-count">
              Showing {filtered.length} of {invoices.length} invoices
            </span>
            <div style={{ display: 'flex', gap: '10px' }}>
              {canGenerateReport && (
                <button onClick={() => setShowReport((v) => !v)} className="report-btn">
                  {showReport ? 'Hide Report' : 'Generate Report'}
                </button>
              )}
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="clear-filters-btn">Clear Filters</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── REPORT PANEL ── */}
      {showReport && reportData && (
        <div className="report-panel">
          <div className="report-panel-header">
            <div>
              <h3>Invoice Report</h3>
              <p className="report-subtitle">
                Period: {fmtDate(reportData.dateFrom)} — {fmtDate(reportData.dateTo)}
              </p>
            </div>
            <button onClick={handleDownloadReport} className="print-report-btn">Download PDF</button>
          </div>

          {/* Printable content */}
          <div>
            <h2 style={{ marginBottom: 4 }}>Invoice Report</h2>
            <p className="report-subtitle">
              Period: {fmtDate(reportData.dateFrom)} — {fmtDate(reportData.dateTo)}
            </p>

            {/* Bills summary */}
            <div className="report-section">
              <h3 className="report-section-title">Bills Summary</h3>
              <div className="report-row-grid">
                <div className="report-card">
                  <div className="card-label">Total Bills</div>
                  <div className="card-value blue">{reportData.total.count}</div>
                </div>
                <div className="report-card">
                  <div className="card-label">Active Bills</div>
                  <div className="card-value green">{reportData.active.count}</div>
                </div>
                <div className="report-card">
                  <div className="card-label">Cancelled Bills</div>
                  <div className="card-value red">{reportData.cancelled.count}</div>
                </div>
                <div className="report-card">
                  <div className="card-label">Effective Tax Rate</div>
                  <div className="card-value">{reportData.effectivePct.toFixed(2)}%</div>
                </div>
              </div>
            </div>

            {/* Amount breakdown table */}
            <div className="report-section">
              <h3 className="report-section-title">Amount Breakdown</h3>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="text-right">Taxable Value</th>
                    <th className="text-right">CGST</th>
                    <th className="text-right">SGST</th>
                    <th className="text-right">IGST</th>
                    <th className="text-right">Total Tax</th>
                    <th className="text-right">Grand Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Total (All Bills)</td>
                    <td className="text-right">{fmt(reportData.total.taxable)}</td>
                    <td className="text-right">{fmt(reportData.total.cgst)}</td>
                    <td className="text-right">{fmt(reportData.total.sgst)}</td>
                    <td className="text-right">{fmt(reportData.total.igst)}</td>
                    <td className="text-right">{fmt(reportData.total.tax)}</td>
                    <td className="text-right">{fmt(reportData.total.amount)}</td>
                  </tr>
                  <tr className="cancelled-row">
                    <td>Cancelled Bills</td>
                    <td className="text-right">{fmt(reportData.cancelled.taxable)}</td>
                    <td className="text-right">{fmt(reportData.cancelled.cgst)}</td>
                    <td className="text-right">{fmt(reportData.cancelled.sgst)}</td>
                    <td className="text-right">{fmt(reportData.cancelled.igst)}</td>
                    <td className="text-right">{fmt(reportData.cancelled.tax)}</td>
                    <td className="text-right">{fmt(reportData.cancelled.amount)}</td>
                  </tr>
                  <tr className="total-row">
                    <td>Balance (Active)</td>
                    <td className="text-right">{fmt(reportData.active.taxable)}</td>
                    <td className="text-right">{fmt(reportData.active.cgst)}</td>
                    <td className="text-right">{fmt(reportData.active.sgst)}</td>
                    <td className="text-right">{fmt(reportData.active.igst)}</td>
                    <td className="text-right">{fmt(reportData.active.tax)}</td>
                    <td className="text-right">{fmt(reportData.active.amount)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="empty-state">
          <p>No invoices found. Create your first invoice!</p>
          <button onClick={() => navigate('/dashboard/invoices/create')} className="create-btn">
            Create Invoice
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <p>No invoices match the current filters.</p>
          <button onClick={clearFilters} className="clear-filters-btn">Clear Filters</button>
        </div>
      ) : (
        <div className="invoices-table">
          <table>
            <thead>
              <tr>
                <th>Invoice No.</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Tax</th>
                <th>Tax %</th>
                <th>Items</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((invoice) => {
                const tax = getTaxTotal(invoice);
                const pct = getTaxPct(invoice);
                return (
                  <tr key={invoice.id} className={invoice.is_cancelled ? 'row-cancelled' : ''}>
                    <td>{invoice.invoice_number}</td>
                    <td>{new Date(invoice.invoice_date).toLocaleDateString()}</td>
                    <td>{invoice.customer_name}</td>
                    <td>₹{parseFloat(invoice.grand_total).toFixed(2)}</td>
                    <td>₹{tax.toFixed(2)}</td>
                    <td>{pct.toFixed(1)}%</td>
                    <td>{invoice.items?.length || 0}</td>
                    <td>
                      {invoice.is_cancelled ? (
                        <span className="status-cancelled">Cancelled</span>
                      ) : (
                        <span className="status-active">Active</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => handleDownload(invoice.id)} className="download-btn" title="Download PDF">Download</button>
                        <button onClick={() => navigate(`/dashboard/invoices/edit/${invoice.id}`)} className="edit-btn" title="Edit">Edit</button>
                        <button onClick={() => handleCancel(invoice)} className={invoice.is_cancelled ? 'uncancel-btn' : 'cancel-btn'} title={invoice.is_cancelled ? 'Uncancel' : 'Cancel'}>
                          {invoice.is_cancelled ? 'Uncancel' : 'Cancel'}
                        </button>
                        <button onClick={() => handleDelete(invoice.id)} className="delete-btn" title="Delete">Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Invoices;
